/* eslint-disable react/no-this-in-sfc */
import getXOrLastGroupNode from "components/charts/modern/helpers/getXOrLastGroupNode";
import classNames from "components/ui/classNames";
import type { TooltipFormatterContextObject } from "highcharts";
import type { ReactElement } from "react";
import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import colors from "tailwindcss/colors";

interface SharesHistoryTooltipProps {
  label?: string;
  context: TooltipFormatterContextObject;
  intl: IntlShape;
}
export default function SharesHistoryTooltip({ label, context, intl }: SharesHistoryTooltipProps): ReactElement {
  const percentageFormatter = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: 1,
    });
  }, []);

  const valueFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumSignificantDigits: 1,
    maximumSignificantDigits: 4,
  });

  if (context.x && typeof context.x !== "string" && context.points && context.points.length > 0) {
    return (
      <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
        <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600">
          {getXOrLastGroupNode(context, intl)}
        </span>

        <div className="space-y-1">
          {context.points
            .sort((a, b) => {
              return (b.y || 0) - (a.y || 0);
            })
            .map(({ y, series }) => {
              if (!y) {
                return null;
              }

              const textColor =
                series.userOptions && series.userOptions.type === "column"
                  ? series.userOptions.borderColor || series.color
                  : series.color;

              const lastY = series.points.length > 0 ? series.points[series.points.length - 1].y : null;
              const returnSoFar = lastY ? lastY / y - 1 : null;

              return (
                <div className="flex items-center justify-between space-x-3">
                  <span
                    className="font-mono font-semibold"
                    style={{
                      color: textColor && typeof textColor === "string" ? textColor : colors.slate[600],
                    }}
                  >
                    {label}
                  </span>

                  <span className="font-mono font-semibold">{valueFormatter.format(y)}</span>

                  {returnSoFar !== null && (
                    <span
                      className={classNames("font-mono font-semibold", {
                        "text-rose-600": returnSoFar < 0,
                        "text-slate-600": returnSoFar === 0,
                        "text-emerald-600": returnSoFar > 0,
                      })}
                    >
                      {percentageFormatter.format(returnSoFar)}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  }

  return <span />;
}
